import { parseWithZod } from '@conform-to/zod'
import { invariantResponse } from '@epic-web/invariant'
import {
	json,
	type LoaderFunctionArgs,
	type ActionFunctionArgs,
	type HeadersFunction,
	type LinksFunction,
	type MetaFunction,
} from '@remix-run/node'
import {
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useFetchers,
	useLoaderData,
} from '@remix-run/react'
import { withSentry } from '@sentry/remix'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { Button } from './components/ui/button.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { type Theme, setTheme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { useOptionalUser } from './utils/user.ts'

export const links: LinksFunction = () => {
	return [
		//These should match the css preloads above to avoid css as render blocking resource
		{ rel: 'icon', type: 'image/svg+xml', href: '/favicons/favicon.svg' },
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	return [
		{ title: data ? 'barrymcgee.dev' : 'Error | barrymcgee.dev' },
		{
			name: 'description',
			content: `Barry McGee — agentic software engineer. Twenty years shipping web products at the BBC, Canonical, FutureLearn, Kahoot and now Shopify. Building with AI and building AI into products.`,
		},
	]
}

export async function loader({ request }: LoaderFunctionArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUniqueOrThrow({
						select: {
							id: true,
							name: true,
							username: true,
							image: { select: { id: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = honeypot.getInputProps()

	return json(
		{
			user,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
	const headers = {
		'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
	}
	return headers
}

const ThemeFormSchema = z.object({
	theme: z.enum(['system', 'light', 'dark']),
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const submission = parseWithZod(formData, {
		schema: ThemeFormSchema,
	})

	invariantResponse(submission.status === 'success', 'Invalid theme received')

	const { theme } = submission.value

	const responseInit = {
		headers: { 'set-cookie': setTheme(theme) },
	}
	return json({ result: submission.reply() }, responseInit)
}

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
	allowIndexing = true,
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string>
	allowIndexing?: boolean
}) {
	return (
		<html lang="en" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const nonce = useNonce()
	const user = useOptionalUser()
	const theme = useTheme()
	const allowIndexing = data.ENV.ALLOW_INDEXING !== 'false'
	useToast(data.toast)

	return (
		<Document
			nonce={nonce}
			theme={theme}
			allowIndexing={allowIndexing}
			env={data.ENV}
		>
			<div className="m-auto flex min-h-screen max-w-[1024px] flex-col justify-between bg-background">
				<header className="container mb-6 py-6">
					<div className="flex flex-wrap items-center justify-between gap-4 sm:flex-nowrap md:gap-8">
						<header className="flex gap-4">
							<Link to="/">
								<img
									src="/img/avatar.avif"
									alt="Barry McGee avatar"
									className="h-[75px] w-[75px] rounded-full object-cover"
								/>
							</Link>
							<div className="flex items-center">
								<div>
									<Link to="/">
										<h1 className="mb-0 text-4xl font-bold">Barry McGee</h1>
									</Link>
									<p className="mb-0 font-mono text-sm font-light tracking-tight text-muted-foreground">
										Agentic Software Engineer
									</p>
								</div>
							</div>
						</header>

						<nav className="flex items-center gap-6">
							<Link
								to="/experience"
								className="text-lg font-medium text-muted-foreground transition-colors hover:text-foreground"
							>
								Experience
							</Link>
							<Link
								to="/contact"
								className="text-lg font-medium text-muted-foreground transition-colors hover:text-foreground"
							>
								Contact
							</Link>
							{user ? <UserDropdown /> : null}
						</nav>
					</div>
				</header>

				<main className="container flex-1">
					<Outlet />
				</main>

				<footer className="container mt-16 w-full py-5 text-sm text-slate-400">
					<div className="m-auto border-t border-solid border-slate-200 md:max-w-[75%]">
						<div className="m-auto flex  justify-between py-[2rem] md:max-w-[50%]">
							<a href="https://bsky.app/profile/barrymcgee.dev">
								<div className="w-6">
									<svg
										width="100%"
										viewBox="0 0 96 85"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M50.1428 64.4373C50.1326 64.4599 50.1026 64.5224 50.0542 64.6026C50.022 64.6558 49.929 64.8063 49.7711 64.9761L49.7698 64.9775C49.7172 65.0341 49.5522 65.2115 49.2773 65.3832C49.1687 65.451 49.043 65.5178 48.9003 65.576C48.857 65.4505 48.8153 65.3292 48.7752 65.2121C48.7181 65.0453 48.6643 64.8874 48.6137 64.7391C48.4061 64.1296 48.2536 63.682 48.1525 63.4468H50.5C50.4999 63.4552 50.4996 63.4899 50.4956 63.5424C50.4923 63.5857 50.4825 63.6991 50.4491 63.8474L50.449 63.8479C50.4391 63.8919 50.4149 63.9996 50.3618 64.1435C50.3004 64.3095 50.2007 64.5235 50.0401 64.7432C50.0332 64.7526 50.0261 64.7621 50.0188 64.7718C50.0783 64.6028 50.1175 64.4968 50.1428 64.4373ZM50.1428 64.4373C50.1458 64.4309 50.1471 64.4277 50.1468 64.4282C50.1467 64.4285 50.1459 64.4302 50.1445 64.4335C50.1439 64.4347 50.1434 64.436 50.1428 64.4373ZM45.8572 64.4374C45.8675 64.4601 45.8975 64.5225 45.9458 64.6026C45.978 64.6558 46.071 64.8063 46.2289 64.9761L46.2302 64.9775C46.2828 65.0341 46.4478 65.2115 46.7227 65.3832C46.8312 65.451 46.9569 65.5178 47.0997 65.576C47.143 65.4505 47.1847 65.3291 47.2248 65.2121C47.2819 65.0453 47.3357 64.8874 47.3863 64.7391L47.3863 64.739C47.5939 64.1296 47.7464 63.682 47.8475 63.4468H45.5C45.5001 63.4552 45.5004 63.4899 45.5044 63.5424C45.5077 63.5857 45.5175 63.6991 45.5509 63.8474L45.551 63.8479C45.5609 63.8919 45.5851 63.9996 45.6382 64.1435C45.6996 64.3095 45.7993 64.5235 45.9599 64.7432C45.9668 64.7526 45.9739 64.7621 45.9812 64.7718C45.9218 64.6029 45.8825 64.4969 45.8572 64.4374ZM45.8572 64.4374C45.8566 64.4361 45.8561 64.4348 45.8555 64.4335C45.8518 64.425 45.8524 64.427 45.8572 64.4374ZM19.3082 7.68893L19.3082 7.68896C24.554 11.6272 30.1058 17.683 34.9199 23.9443C39.7282 30.1979 43.6712 36.487 45.7493 40.7851L48 45.4401L50.2507 40.7851C52.3288 36.4871 56.2718 30.198 61.0801 23.9443C65.8942 17.683 71.446 11.6272 76.6917 7.68882C80.5336 4.80467 85.2062 2.55241 88.6112 2.50095C90.1957 2.47701 91.2542 2.92378 91.9743 3.75648C92.7565 4.66113 93.5 6.43418 93.5 9.79385C93.5 11.127 93.0925 17.1618 92.5458 23.4301C92.2751 26.5336 91.9748 29.6425 91.6823 32.186C91.3791 34.8214 91.1077 36.6204 90.9258 37.2702L90.9258 37.2703C89.4383 42.586 86.0418 45.8695 81.7345 47.6795C77.3434 49.5247 71.9494 49.846 66.7316 48.958L66.3122 51.4225L65.8927 53.8871C75.362 55.4986 80.4384 58.8535 82.2146 62.2999C83.836 65.4457 83.1808 69.7715 78.2095 74.8726C73.3507 79.8584 69.5095 81.6838 66.6134 81.9812C63.8175 82.2682 61.4252 81.1758 59.2748 79.1517C57.0813 77.087 55.2875 74.1743 53.8798 71.2459C52.4844 68.3434 51.5461 65.5941 50.9801 63.9328L50.9447 63.8287C50.7683 63.3105 50.5893 62.7848 50.4483 62.4569C50.4094 62.3666 50.3339 62.1966 50.2256 62.0173C50.1746 61.9328 50.0643 61.7582 49.8907 61.5715C49.7825 61.4551 49.3017 60.9399 48.4622 60.7956C48.3022 60.7681 48.1481 60.7561 48 60.7574C47.8519 60.7561 47.6978 60.7681 47.5378 60.7956C46.6983 60.9399 46.2175 61.4551 46.1093 61.5715C45.9357 61.7582 45.8254 61.9328 45.7744 62.0173C45.6661 62.1966 45.5906 62.3666 45.5517 62.4569C45.4107 62.7847 45.2318 63.3102 45.0554 63.8284L45.0199 63.9328C44.4539 65.5941 43.5156 68.3434 42.1202 71.2459C40.7125 74.1743 38.9187 77.087 36.7252 79.1517C34.5748 81.1758 32.1825 82.2682 29.3866 81.9812C26.4905 81.6838 22.6493 79.8584 17.7905 74.8726C12.8192 69.7715 12.1639 65.4457 13.7853 62.2999C15.5615 58.8535 20.6379 55.4985 30.1073 53.8869L29.6878 51.4224L29.2684 48.9578C24.0506 49.8458 18.6566 49.5246 14.2655 47.6793C9.95818 45.8693 6.56174 42.5858 5.07422 37.2701L5.0742 37.27C4.89233 36.6202 4.62089 34.8213 4.31775 32.1858C4.02516 29.6423 3.72489 26.5334 3.4542 23.4299C2.90748 17.1615 2.5 11.1267 2.5 9.79368C2.5 6.43406 3.24353 4.66104 4.02576 3.75641C4.74578 2.92371 5.80429 2.47694 7.38882 2.50089C10.7939 2.55236 15.4665 4.80465 19.3082 7.68893ZM2.66684 37.9438L2.6667 37.9438L2.66684 37.9438Z"
											fill="white"
											fillOpacity="0.2"
											stroke="#000000"
											strokeWidth="5"
										/>
									</svg>
								</div>
							</a>
							<a href="https://x.com/barrymcgee">
								<svg
									width="24px"
									height="24px"
									viewBox="0 0 24 24"
									strokeWidth="1.5"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
									className="stroke-[#000000] dark:stroke-[#94a3b8]"
								>
									<path d="M16.8198 20.7684L3.75317 3.96836C3.44664 3.57425 3.72749 3 4.22678 3H6.70655C6.8917 3 7.06649 3.08548 7.18016 3.23164L20.2468 20.0316C20.5534 20.4258 20.2725 21 19.7732 21H17.2935C17.1083 21 16.9335 20.9145 16.8198 20.7684Z"></path>
									<path d="M20 3L4 21" strokeLinecap="round"></path>
								</svg>
							</a>
							<a href="https://github.com/barrymcgee/">
								<svg
									width="24px"
									height="24px"
									strokeWidth="1.5"
									viewBox="0 0 24 24"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
									className="stroke-[#000000] dark:stroke-[#94a3b8]"
								>
									<path
										d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
										strokeLinecap="round"
										strokeLinejoin="round"
									></path>
									<path
										d="M14.3333 19V17.137C14.3583 16.8275 14.3154 16.5163 14.2073 16.2242C14.0993 15.9321 13.9286 15.6657 13.7067 15.4428C15.8 15.2156 18 14.4431 18 10.8989C17.9998 9.99256 17.6418 9.12101 17 8.46461C17.3039 7.67171 17.2824 6.79528 16.94 6.01739C16.94 6.01739 16.1533 5.7902 14.3333 6.97811C12.8053 6.57488 11.1947 6.57488 9.66666 6.97811C7.84666 5.7902 7.05999 6.01739 7.05999 6.01739C6.71757 6.79528 6.69609 7.67171 6.99999 8.46461C6.35341 9.12588 5.99501 10.0053 5.99999 10.9183C5.99999 14.4366 8.19999 15.2091 10.2933 15.4622C10.074 15.6829 9.90483 15.9461 9.79686 16.2347C9.68889 16.5232 9.64453 16.8306 9.66666 17.137V19"
										strokeLinecap="round"
										strokeLinejoin="round"
									></path>
									<path
										d="M9.66667 17.7018C7.66667 18.3335 6 17.7018 5 15.7544"
										strokeLinecap="round"
										strokeLinejoin="round"
									></path>
								</svg>
							</a>
							<a href="https://linkedin.com/in/barrymcgee">
								<svg
									width="24px"
									height="24px"
									strokeWidth="1.5"
									viewBox="0 0 24 24"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
									className="stroke-[#000000] dark:stroke-[#94a3b8]"
								>
									<path
										d="M21 8V16C21 18.7614 18.7614 21 16 21H8C5.23858 21 3 18.7614 3 16V8C3 5.23858 5.23858 3 8 3H16C18.7614 3 21 5.23858 21 8Z"
										strokeLinecap="round"
										strokeLinejoin="round"
									></path>
									<path
										d="M7 17V13.5V10"
										strokeLinecap="round"
										strokeLinejoin="round"
									></path>
									<path
										d="M11 17V13.75M11 10V13.75M11 13.75C11 10 17 10 17 13.75V17"
										strokeLinecap="round"
										strokeLinejoin="round"
									></path>
									<path
										d="M7 7.01L7.01 6.99889"
										strokeLinecap="round"
										strokeLinejoin="round"
									></path>
								</svg>
							</a>
							<a href="https://instagram.com/barrymcgee">
								<svg
									width="24px"
									height="24px"
									strokeWidth="1.5"
									viewBox="0 0 24 24"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
									className="stroke-[#000000] dark:stroke-[#94a3b8]"
								>
									<path
										d="M12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16Z"
										strokeLinecap="round"
										strokeLinejoin="round"
									></path>
									<path d="M3 16V8C3 5.23858 5.23858 3 8 3H16C18.7614 3 21 5.23858 21 8V16C21 18.7614 18.7614 21 16 21H8C5.23858 21 3 18.7614 3 16Z"></path>
									<path
										d="M17.5 6.51L17.51 6.49889"
										strokeLinecap="round"
										strokeLinejoin="round"
									></path>
								</svg>
							</a>
						</div>
					</div>

					<p className="text-center text-sm">
						{new Date().getFullYear()} Copyright - all thoughts my own.
					</p>

					{/* <ThemeSwitch userPreference={data.requestInfo.userPrefs.theme} /> */}
				</footer>
				<script
					defer
					src="https://cloud.umami.is/script.js"
					data-website-id="403b29b4-87fc-4811-92e4-9f33b1cb17ae"
				></script>
			</div>
			<EpicToaster closeButton position="top-center" theme={theme} />
			<EpicProgress />
		</Document>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default withSentry(AppWithProviders)

function UserDropdown() {
	return (
		<Button
			asChild
			variant="outline"
			className="border-gray-400 text-gray-700 hover:bg-gray-50"
		>
			<Link to="/admin">Admin</Link>
		</Button>
	)
}

/**
 * @returns the user's theme preference, or the client hint theme if the user
 * has not set a preference.
 */
export function useTheme(): Theme {
	return 'light' as Theme
}

/**
 * If the user's changing their theme mode preference, this will return the
 * value it's being changed to.
 */
export function useOptimisticThemeMode() {
	const fetchers = useFetchers()
	const themeFetcher = fetchers.find(f => f.formAction === '/')

	if (themeFetcher && themeFetcher.formData) {
		const submission = parseWithZod(themeFetcher.formData, {
			schema: ThemeFormSchema,
		})

		if (submission.status === 'success') {
			return submission.value.theme
		}
	}
}

export function ErrorBoundary() {
	// the nonce doesn't rely on the loader so we can access that
	const nonce = useNonce()

	// NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
	// likely failed to run so we have to do the best we can.
	// We could probably do better than this (it's possible the loader did run).
	// This would require a change in Remix.

	// Just make sure your root route never errors out and you'll always be able
	// to give the user a better UX.

	return (
		<Document nonce={nonce}>
			<GeneralErrorBoundary />
		</Document>
	)
}
